import { useState, type CSSProperties } from "react";
import { mcpClient } from "../../../lib/mcp-client";
import { showToast } from "../primitives";
import { humanizeArgName } from "../command-palette/format";
import type { PromptArgDef } from "../../../lib/types";

/**
 * Sprint 11 (S11.40) — test-compose panel for the prompt editor. Generates a
 * form from the declared arguments, composes the prompt via `prompts/get`
 * (real compose engine — args → {{?tool}} directives → [[ref]] embeds), and
 * shows the output. Directives call real read-only tools against the live
 * workspace, which is acceptable (side-effect-free).
 */

interface Props {
  promptName: string;
  args: PromptArgDef[];
  /** Save pending changes before composing (the server composes the saved body). */
  onBeforeCompose?: () => Promise<void>;
}

export function PromptTestCompose({ promptName, args, onBeforeCompose }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [output, setOutput] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missingRequired = args.some((a) => a.required && !values[a.name]?.trim());

  async function compose() {
    if (missingRequired) return;
    setComposing(true);
    setError(null);
    try {
      await onBeforeCompose?.();
      const clean: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        if (v && v.trim()) clean[k] = v.trim();
      }
      const text = await mcpClient.getComposedPrompt(promptName, clean);
      setOutput(text);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Compose failed");
    } finally {
      setComposing(false);
    }
  }

  async function copy() {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      showToast("Composed prompt copied");
    } catch {
      showToast("Couldn't copy", "error", true);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {args.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {args.map((arg) => (
            <label key={arg.name} style={fieldStyle}>
              <span style={labelStyle}>
                {humanizeArgName(arg.name)}
                {arg.required ? <span style={{ color: "var(--c-red-600)" }}> *</span> : null}
                <span style={{ color: "var(--color-text-tertiary)", fontWeight: 400 }}> · {arg.type}</span>
              </span>
              <input
                style={inputStyle}
                value={values[arg.name] ?? ""}
                onChange={(e) => setValues((v) => ({ ...v, [arg.name]: e.target.value }))}
                placeholder={arg.description ?? ""}
              />
            </label>
          ))}
        </div>
      )}

      <div>
        <button
          type="button"
          data-variant="primary"
          disabled={composing || missingRequired}
          onClick={() => void compose()}
          style={{ fontSize: 13, padding: "8px 14px" }}
        >
          {composing ? "Composing…" : "Preview compose"}
        </button>
      </div>

      {error ? (
        <pre style={{ ...codeBlock, color: "var(--c-red-600)" }}>{error}</pre>
      ) : output !== null ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: "var(--color-text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>
              Composed output
            </span>
            <button type="button" onClick={() => void copy()} style={copyBtn}>
              <i className="ti ti-copy" style={{ fontSize: 13, marginRight: 3 }} aria-hidden="true" /> Copy
            </button>
          </div>
          <pre style={codeBlock}>{output || "(empty)"}</pre>
        </div>
      ) : null}
    </div>
  );
}

const fieldStyle: CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const labelStyle: CSSProperties = { fontSize: 12, fontWeight: 500, color: "var(--color-text-secondary)" };
const inputStyle: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};
const codeBlock: CSSProperties = {
  fontFamily: "var(--font-mono, ui-monospace, monospace)",
  fontSize: 12,
  lineHeight: 1.5,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  background: "var(--color-background-secondary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-md)",
  padding: 14,
  maxHeight: "40vh",
  overflowY: "auto",
  margin: 0,
  color: "var(--color-text-primary)",
};
const copyBtn: CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontSize: 12,
  fontWeight: 500,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  fontFamily: "var(--font-sans)",
};
